import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackRouteChange } from "@/lib/analytics";

/**
 * Scrolls to top on every route change and fires SPA analytics.
 *
 * Uses instant scroll (not smooth) to ensure users always land at
 * the top of the new page without a visible scroll animation.
 * Hash-based navigation (#section) is excluded so in-page anchors work.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    let frame: number | undefined;
    // Don't scroll if navigating to an in-page anchor
    if (!hash) {
      const resetScroll = () => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };
      resetScroll();
      frame = window.requestAnimationFrame(resetScroll);
    }

    // Skip tracking on initial render — initAnalytics handles it
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      trackRouteChange(pathname);
    }

    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
